import { useState } from 'react'
import { Check, LayoutGrid, Plus, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import {
  ADD_WIDGETS_FILTERS,
  MAX_WIDGETS_PER_DASHBOARD,
  addWidgetsRows,
  setWidgetShown,
  type AddWidgetsRow,
  type WidgetPlacement,
} from '@/lib/dashboardLayouts'
import { dashboardModule, type DashboardWidgetDef, type WidgetSize } from '@/lib/dashboardWidgets'
import { cn } from '@/lib/utils'
import { isWidgetBuilt } from './builtWidgets'
import { moduleStyle } from './moduleStyle'

const groupOf = (row: AddWidgetsRow) => dashboardModule(row.widget.module)?.group ?? ''

/**
 * The widget gallery for the dashboard being edited: module groups down the
 * side, search on top, and each widget as a card with what it shows, the
 * module it comes from and its size. Widgets in the catalog that are not built
 * yet are listed (so the user can see what is coming) but cannot be added.
 */
export function AddWidgetsDialog({
  open,
  onOpenChange,
  draft,
  allowed,
  onChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  draft: WidgetPlacement[]
  /** Every widget the user may see; the gallery never offers anything else. */
  allowed: DashboardWidgetDef[]
  onChange: (draft: WidgetPlacement[]) => void
}) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('all')
  const everything = addWidgetsRows(draft, allowed, isWidgetBuilt, query, 'all')
  const rows = filter === 'all' ? everything : everything.filter(r => groupOf(r) === filter)
  const full = draft.length >= MAX_WIDGETS_PER_DASHBOARD
  const toggle = (id: string, on: boolean) => onChange(setWidgetShown(draft, id, on))

  // In "All", widgets sit under their group's heading; in a group, one list.
  const sections = (filter === 'all' ? ADD_WIDGETS_FILTERS.filter(f => f.id !== 'all') : ADD_WIDGETS_FILTERS.filter(f => f.id === filter))
    .map(f => ({ ...f, rows: rows.filter(r => groupOf(r) === f.id) }))
    .filter(s => s.rows.length > 0)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex h-[min(85vh,720px)] max-w-5xl gap-0 overflow-hidden p-0 [&>button]:hidden'>
        {/* Side: what to browse */}
        <aside className='hidden w-56 shrink-0 flex-col border-r border-gray-200 bg-gray-50/80 p-4 sm:flex'>
          <div className='mb-4 flex items-center gap-2'>
            <span className='flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600'>
              <LayoutGrid className='h-4 w-4 text-white' />
            </span>
            <div>
              <DialogTitle className='text-sm font-semibold'>Add widgets</DialogTitle>
              <DialogDescription className='text-[11px] text-gray-500'>Choose what this dashboard shows</DialogDescription>
            </div>
          </div>
          <nav aria-label='Widget groups' className='flex flex-col gap-0.5'>
            {ADD_WIDGETS_FILTERS.map(f => {
              const inGroup = f.id === 'all' ? everything : everything.filter(r => groupOf(r) === f.id)
              const added = inGroup.filter(r => r.shown).length
              return (
                <button
                  key={f.id}
                  type='button'
                  aria-pressed={filter === f.id}
                  onClick={() => setFilter(f.id)}
                  className={cn(
                    'flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors',
                    filter === f.id ? 'bg-white font-semibold text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-600 hover:bg-white/70',
                  )}
                >
                  <span className='truncate'>{f.id === 'all' ? 'All widgets' : f.title}</span>
                  <span className='ml-2 shrink-0 text-[11px] tabular-nums text-gray-400'>
                    {added > 0 ? `${added}/` : ''}
                    {inGroup.length}
                  </span>
                </button>
              )
            })}
          </nav>
          <div className='mt-auto rounded-lg border border-blue-100 bg-blue-50 p-3 text-[11px] leading-relaxed text-blue-800'>
            <span className='font-semibold tabular-nums'>{draft.length}</span> on this dashboard. After adding, drag widgets to move
            them and use S, W or F to resize.
          </div>
        </aside>

        {/* Main: search and the gallery */}
        <div className='flex min-w-0 flex-1 flex-col'>
          <div className='flex items-center gap-3 border-b border-gray-200 px-5 py-3'>
            <div className='relative flex-1'>
              <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder='Search widgets, e.g. "overdue" or "inventory"'
                aria-label='Search widgets'
                className='h-10 w-full rounded-full border border-gray-200 bg-gray-50 pl-9 pr-9 text-sm outline-none transition-colors placeholder:text-gray-400 focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100'
              />
              {query && (
                <button
                  type='button'
                  aria-label='Clear search'
                  onClick={() => setQuery('')}
                  className='absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                >
                  <X className='h-3.5 w-3.5' />
                </button>
              )}
            </div>
            {/* Group filter for narrow screens, where the side panel is hidden. */}
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              aria-label='Widget group'
              className='h-10 rounded-full border border-gray-200 bg-white px-3 text-sm sm:hidden'
            >
              {ADD_WIDGETS_FILTERS.map(f => (
                <option key={f.id} value={f.id}>
                  {f.id === 'all' ? 'All widgets' : f.title}
                </option>
              ))}
            </select>
            <Button size='sm' onClick={() => onOpenChange(false)} className='rounded-full px-5'>
              Done
            </Button>
          </div>

          {full && (
            <p className='border-b border-amber-100 bg-amber-50 px-5 py-2 text-xs text-amber-800'>
              This dashboard holds the most widgets it can ({MAX_WIDGETS_PER_DASHBOARD}). Remove one to add another.
            </p>
          )}

          <div className='flex-1 overflow-y-auto px-5 py-4'>
            {sections.length === 0 && (
              <div className='flex flex-col items-center gap-2 py-16 text-center'>
                <Search className='h-6 w-6 text-gray-300' />
                <p className='text-sm text-gray-500'>No widgets match “{query}”.</p>
              </div>
            )}
            {sections.map(section => (
              <section key={section.id} className='mb-6 last:mb-0'>
                {filter === 'all' && (
                  <h3 className='mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400'>{section.title}</h3>
                )}
                <ul className='grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3'>
                  {section.rows.map(row => (
                    <WidgetTile key={row.widget.id} row={row} disabled={full && !row.shown} onToggle={toggle} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function WidgetTile({
  row,
  disabled,
  onToggle,
}: {
  row: AddWidgetsRow
  disabled: boolean
  onToggle: (id: string, on: boolean) => void
}) {
  const { widget, moduleLabel, shown, built } = row
  const style = moduleStyle(widget.module)
  const Icon = style?.icon
  return (
    <li
      className={cn(
        'group relative flex flex-col gap-3 rounded-xl border bg-white p-4 transition-all',
        shown ? 'border-blue-300 ring-2 ring-blue-100' : 'border-gray-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md',
        !built && 'opacity-60 hover:translate-y-0 hover:shadow-none',
      )}
    >
      <div className='flex items-start gap-3'>
        {Icon && (
          <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', built ? style.tile : 'bg-gray-300')}>
            <Icon className='h-4 w-4 text-white' />
          </span>
        )}
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-semibold leading-snug text-gray-900'>{widget.title}</p>
          <p className='mt-0.5 line-clamp-2 text-xs leading-relaxed text-gray-500'>{widget.description}</p>
        </div>
      </div>
      <div className='mt-auto flex items-center justify-between gap-2'>
        <div className='flex min-w-0 items-center gap-2 text-[11px] text-gray-400'>
          <span className='truncate'>{moduleLabel}</span>
          <SizeGlyph size={widget.defaultSize} />
        </div>
        {!built ? (
          <span className='shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-500'>Coming soon</span>
        ) : shown ? (
          <button
            type='button'
            onClick={() => onToggle(widget.id, false)}
            aria-label={`Remove ${widget.title}`}
            className='flex shrink-0 items-center gap-1 rounded-full bg-blue-600 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-red-600'
          >
            <Check className='h-3.5 w-3.5 group-hover:hidden' />
            <X className='hidden h-3.5 w-3.5 group-hover:block' />
            <span className='group-hover:hidden'>Added</span>
            <span className='hidden group-hover:inline'>Remove</span>
          </button>
        ) : (
          <button
            type='button'
            onClick={() => onToggle(widget.id, true)}
            disabled={disabled}
            aria-label={`Add ${widget.title}`}
            className='flex shrink-0 items-center gap-1 rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40'
          >
            <Plus className='h-3.5 w-3.5' /> Add
          </button>
        )}
      </div>
    </li>
  )
}

const SIZE_COLS: Record<WidgetSize, number> = { small: 1, wide: 2, full: 4 }
const SIZE_NAME: Record<WidgetSize, string> = { small: 'Small', wide: 'Wide', full: 'Full width' }

/** A tiny picture of how much of the row the widget takes by default. */
function SizeGlyph({ size }: { size: WidgetSize }) {
  return (
    <span className='flex items-center gap-1' title={`${SIZE_NAME[size]} by default`}>
      <span className='flex gap-px' aria-hidden>
        {[0, 1, 2, 3].map(i => (
          <span key={i} className={cn('h-2 w-1.5 rounded-[1px]', i < SIZE_COLS[size] ? 'bg-gray-400' : 'bg-gray-200')} />
        ))}
      </span>
      <span className='sr-only'>{SIZE_NAME[size]} by default</span>
    </span>
  )
}
